/* eslint-disable react-refresh/only-export-components */
// src/context/SubcontractorContext.jsx
import { uuid } from '../lib/uuid';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { SEED_SUBCONTRACTORS, SEED_JOBS, SEED_REMINDERS } from '../components/subcontractors/subcontractorsData';

const SUBS_KEY      = 'wm-subcontractors-v1';
const JOBS_KEY      = 'wm-sub-jobs-v1';
const REMINDERS_KEY = 'wm-sub-reminders-v1';

// ─── Storage helpers ───────────────────────────────────────────────────────────
function load(key, seed) {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
    localStorage.setItem(key, JSON.stringify(seed));
    return seed;
  } catch {
    return seed;
  }
}

function save(key, data) {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch { /* quota */ }
}

// ─── Context ──────────────────────────────────────────────────────────────────
const SubcontractorContext = createContext(null);

export function useSubcontractors() {
  const ctx = useContext(SubcontractorContext);
  if (!ctx) throw new Error('useSubcontractors must be used within SubcontractorProvider');
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export function SubcontractorProvider({ children }) {
  const [subcontractors, setSubcontractors] = useState(() => load(SUBS_KEY,      SEED_SUBCONTRACTORS));
  const [jobs,           setJobs]           = useState(() => load(JOBS_KEY,      SEED_JOBS));
  const [reminders,      setReminders]      = useState(() => load(REMINDERS_KEY, SEED_REMINDERS));

  // Write-through on every change
  useEffect(() => { save(SUBS_KEY,      subcontractors); }, [subcontractors]);
  useEffect(() => { save(JOBS_KEY,      jobs);           }, [jobs]);
  useEffect(() => { save(REMINDERS_KEY, reminders);      }, [reminders]);

  // Cross-tab sync
  useEffect(() => {
    function onStorage(e) {
      if (e.key === SUBS_KEY      && e.newValue) setSubcontractors(JSON.parse(e.newValue) ?? []);
      if (e.key === JOBS_KEY      && e.newValue) setJobs(JSON.parse(e.newValue) ?? []);
      if (e.key === REMINDERS_KEY && e.newValue) setReminders(JSON.parse(e.newValue) ?? []);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ─── Subcontractor CRUD ─────────────────────────────────────────────────────
  const addSubcontractor = useCallback((data) => {
    const now = new Date().toISOString();
    setSubcontractors(prev => [
      { ...data, id: uuid(), createdAt: now, updatedAt: now },
      ...prev,
    ]);
  }, []);

  const updateSubcontractor = useCallback((id, patch) => {
    setSubcontractors(prev =>
      prev.map(s => s.id === id ? { ...s, ...patch, updatedAt: new Date().toISOString() } : s)
    );
  }, []);

  const deleteSubcontractor = useCallback((id) => {
    setSubcontractors(prev => prev.filter(s => s.id !== id));
    setJobs(prev => prev.filter(j => j.subcontractorId !== id));
    setReminders(prev => prev.filter(r => r.subcontractorId !== id));
  }, []);

  // ─── Job CRUD ───────────────────────────────────────────────────────────────
  const addJob = useCallback((data) => {
    const now = new Date().toISOString();
    setJobs(prev => [
      { payments: [], review: null, ...data, id: uuid(), createdAt: now, updatedAt: now },
      ...prev,
    ]);
  }, []);

  const updateJob = useCallback((id, patch) => {
    setJobs(prev =>
      prev.map(j => j.id === id ? { ...j, ...patch, updatedAt: new Date().toISOString() } : j)
    );
  }, []);

  const deleteJob = useCallback((id) => {
    setJobs(prev => prev.filter(j => j.id !== id));
    setReminders(prev => prev.filter(r => r.jobId !== id));
  }, []);

  // ─── Payment CRUD ───────────────────────────────────────────────────────────
  const addPayment = useCallback((jobId, paymentData) => {
    const now = new Date().toISOString();
    const payment = { ...paymentData, id: uuid(), recordedAt: now };
    setJobs(prev =>
      prev.map(j =>
        j.id === jobId
          ? { ...j, payments: [...j.payments, payment], updatedAt: now }
          : j
      )
    );
  }, []);

  const deletePayment = useCallback((jobId, paymentId) => {
    setJobs(prev =>
      prev.map(j =>
        j.id === jobId
          ? { ...j, payments: j.payments.filter(p => p.id !== paymentId), updatedAt: new Date().toISOString() }
          : j
      )
    );
  }, []);

  // ─── Reminder CRUD ──────────────────────────────────────────────────────────
  const addReminder = useCallback((data) => {
    const now = new Date().toISOString();
    setReminders(prev => [
      { done: false, ...data, id: uuid(), createdAt: now },
      ...prev,
    ]);
  }, []);

  const toggleReminder = useCallback((id) => {
    setReminders(prev =>
      prev.map(r => r.id === id ? { ...r, done: !r.done } : r)
    );
  }, []);

  const deleteReminder = useCallback((id) => {
    setReminders(prev => prev.filter(r => r.id !== id));
  }, []);

  // ─── Computed helpers ────────────────────────────────────────────────────────
  const getSubJobs = useCallback((subId) =>
    jobs.filter(j => j.subcontractorId === subId),
  [jobs]);

  const getJobPaymentSummary = useCallback((job) => {
    const amountPaid = job.payments.reduce((s, p) => s + p.amount, 0);
    const amountOwed = job.agreedPrice - amountPaid;
    const overdueMs  = 7 * 86_400_000;
    const isOverdue  = amountOwed > 0 && job.scheduledDate
      && (Date.now() - new Date(job.scheduledDate).getTime()) > overdueMs;

    // overdue takes precedence over partial so a partial-paid-but-overdue job is correctly flagged
    let status;
    if (amountOwed <= 0)   status = 'paid';
    else if (isOverdue)    status = 'overdue';
    else if (amountPaid > 0) status = 'partial';
    else                   status = 'pending';

    return { amountPaid, amountOwed, status };
  }, []);

  const getSubRating = useCallback((subId) => {
    const reviewed = jobs.filter(j => j.subcontractorId === subId && j.review?.rating != null);
    if (!reviewed.length) return null;
    const avg = reviewed.reduce((s, j) => s + j.review.rating, 0) / reviewed.length;
    return Math.round(avg * 2) / 2; // round to nearest 0.5
  }, [jobs]);

  // Read linked records directly from localStorage (no hook dependency)
  const getLinkedEstimate = useCallback((job) => {
    if (job.linkType !== 'estimate' || !job.estimateId) return null;
    try {
      const estimates = JSON.parse(localStorage.getItem('wm-estimates-v2') || '[]');
      return estimates.find(e => e.id === job.estimateId) ?? null;
    } catch { return null; }
  }, []);

  const getLinkedWorkOrder = useCallback((job) => {
    if (job.linkType !== 'work_order' || !job.workOrderId) return null;
    try {
      const orders = JSON.parse(localStorage.getItem('wm-work-orders-v1') || '[]');
      return orders.find(o => o.id === job.workOrderId) ?? null;
    } catch { return null; }
  }, []);

  const value = {
    subcontractors, jobs, reminders,
    addSubcontractor, updateSubcontractor, deleteSubcontractor,
    addJob, updateJob, deleteJob,
    addPayment, deletePayment,
    addReminder, toggleReminder, deleteReminder,
    getSubJobs, getJobPaymentSummary, getSubRating,
    getLinkedEstimate, getLinkedWorkOrder,
  };

  return (
    <SubcontractorContext.Provider value={value}>
      {children}
    </SubcontractorContext.Provider>
  );
}
